import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { OneLine } from "@/components/ui/one-line";
import { askOrb } from "@/components/orbLive";
import { useSessions } from "@/hooks/useSessions";
import { useMilestones, useRecap } from "@/hooks/useEngagement";
import { LESSON_PARAGRAPHS, LESSONS, lessonsForNow, lessonToShow, lossContext, type LessonId, type LossContext } from "@/lib/lessons.core";

/****************
 * LESSONS TIED TO THE MOMENT, ON THE WEB. 2026-10-21, engagement second pass.
 * The rules are lib/lessons.core.ts (twin of the phone's); the texts are
 * common.json lesson.*. Read lessons are remembered on this browser.
 ****************/
import { subscriptionApi } from "@/api/client";

const SEEN_KEY = "copyfi.lessons.seen";

function readSeen(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); } catch { return new Set(); }
}
function markSeen(id: string) {
  try {
    const s = readSeen(); s.add(id);
    localStorage.setItem(SEEN_KEY, JSON.stringify([...s]));
    // On the server too (Guillaume 10-25): every device, and the Orb, know what was read.
    void subscriptionApi.putPreference(SEEN_KEY, [...s]).catch(() => undefined);
  } catch { /* this visit only */ }
}

/** Merges the server's copy into this browser's once per visit, and sends the union back. */
let seenSynced = false;
export function syncSeenLessons(): void {
  if (seenSynced) return;
  seenSynced = true;
  void subscriptionApi.getPreference(SEEN_KEY).then((res) => {
    const server = Array.isArray(res?.value) ? (res.value as string[]) : [];
    const s = readSeen();
    const before = s.size;
    server.forEach((id) => s.add(id));
    try { localStorage.setItem(SEEN_KEY, JSON.stringify([...s])); } catch { /* ignore */ }
    if (s.size !== server.length || before !== s.size) void subscriptionApi.putPreference(SEEN_KEY, [...s]).catch(() => undefined);
  }).catch(() => { seenSynced = false; });
}

/** Which lessons are unlocked now, and the loss context, from the user's own data. */
export function useLessons() {
  useEffect(() => { syncSeenLessons(); }, []);
  const ms = useMilestones();
  const recap = useRecap();
  // Until the sessions arrive, no kill-switch lesson (never a false "none").
  const { data: sessions, isLoading: sessionsLoading } = useSessions();
  const killSwitched = !sessionsLoading && !!sessions?.some((s) => s.kill_switch_triggered && !s.deleted);
  const unlocked = lessonsForNow(ms.data, recap.data, killSwitched);
  return { unlocked, context: lossContext(recap.data) };
}

/** One lesson, a page of its own (Guillaume 10-25: "some articles are just a pop-up"). /learn/lessons/:id */
export function LessonPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation("common");
  const { t: tl } = useTranslation("learn");
  const { context } = useLessons();
  const lesson = (LESSONS as readonly string[]).includes(id) ? (id as LessonId) : null;
  useEffect(() => { if (lesson) markSeen(lesson); }, [lesson]);
  if (!lesson) return <Navigate to="/learn/articles" replace />;
  const title = t(`lesson.${lesson}.title`);
  return (
    <article className="p-6 lg:p-10 max-w-3xl mx-auto w-full" data-testid="learn-lesson">
      <Link to="/learn/articles" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft size={14} className="rtl:rotate-180" />{tl("art.back")}
      </Link>
      <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-6">{t("lesson.minutes")}</p>
      <div className="space-y-4 text-[15px] leading-relaxed">
        {Array.from({ length: LESSON_PARAGRAPHS[lesson] }, (_, i) => <p key={i}>{t(`lesson.${lesson}.p${i + 1}`)}</p>)}
        {lesson === "lossesArePart" && context && (
          <p className={`rounded-lg p-3 border ${context.verdict === "within" ? "border-white/10 bg-white/[0.03]" : "border-warning/40 bg-warning/5"}`}>
            {t(`lesson.context.${context.verdict}`, {
              loss: context.lossPct.toFixed(1), worst: context.traderWorstPct.toFixed(1),
              win: context.winRate.toFixed(0), n: context.trades, trader: t("lesson.thisTrader"),
            })}
          </p>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mt-8">
        <Button variant="outline" onClick={() => askOrb(t("lesson.orbAsk", { title }))}>{t("lesson.askOrb")}</Button>
      </div>
    </article>
  );
}

/** The Dashboard's card: the first unlocked lesson not yet read here. */
export function LessonCard() {
  const { t } = useTranslation("common");
  const { unlocked, context } = useLessons();
  const [seen] = useState(readSeen);
  void context;
  const id = lessonToShow(unlocked, seen);
  if (!id) return null;
  return (
    <>
      <Link to={`/learn/lessons/${id}`}
        className="glass-card rounded-xl p-5 flex items-center gap-4 text-start hover:border-primary/40 transition-colors w-full">
        <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
          <BookOpen size={20} weight="fill" className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <OneLine className="text-xs uppercase tracking-wider text-muted-foreground">{t("lesson.card")}</OneLine>
          <OneLine className="text-sm font-medium">{t(`lesson.${id}.title`)}</OneLine>
        </div>
        <ArrowRight size={18} className="text-muted-foreground shrink-0" />
      </Link>
    </>
  );
}

/** The Learn page's section: lessons for now, then all of them. */
export function LessonsSection() {
  const { t } = useTranslation("common");
  const { unlocked } = useLessons();
  const row = (id: LessonId) => (
    <Link key={id} to={`/learn/lessons/${id}`}
      className="glass-card rounded-xl p-4 flex items-center gap-3 text-start hover:border-primary/40 transition-colors">
      <BookOpen size={18} className="text-primary shrink-0" />
      <OneLine className="text-sm font-medium flex-1">{t(`lesson.${id}.title`)}</OneLine>
      <span className="text-xs text-muted-foreground whitespace-nowrap">{t("lesson.minutes")}</span>
    </Link>
  );
  return (
    <section className="space-y-6 mb-10">
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t("lesson.forYou")}</h2>
        {unlocked.length ? <div className="grid gap-3 md:grid-cols-2">{unlocked.map(row)}</div>
          : <p className="text-sm text-muted-foreground">{t("lesson.forYouEmpty")}</p>}
      </div>
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t("lesson.all")}</h2>
        <div className="grid gap-3 md:grid-cols-2">{LESSONS.filter((id) => !unlocked.includes(id)).map(row)}</div>
      </div>
    </section>
  );
}
